import Controller, { OnAfterFetch, OnBeforeSubmit, OnBlur, OnCancel, OnChange, OnClick, OnFocus, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ActionAPI, AutocompleteAPI, FieldAPI, FormAPI, InputAPI, Metadata, ModalProps, ModalService, PanelAPI, TextAreaAPI } from '@philips-internal/oswald-ui-components';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { AbortableSubmitEvent,ChangeEvent, FetchEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HealthProfessionalUser } from '../../../app/authentication';
import { HttpResponse, HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { Inject } from '@odinjs/odin';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';

import { Endpoint } from '../../../constants/endpoints';
import { ISO_FULL_DATE_FORMAT } from '../../../constants/date-utils';

import { VitalSignAttributes } from '../utils/attribute-validate-numbers';
import BaseController from '../../BaseController';
import CalculateIMC from '../services/vital-sign-calculate-imc-service';
import DialogService from '../../../services/dialog-service';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import moment from 'moment';
import ShowToast from '../../../utils/toast-service';
import VitalSignService from '../services/vital-sign-service';

import './vital-sign-create-and-edit-controller.css';
import { FragmentNames, VitalSignCreateAndEditAliases } from '../../../codegen';
import { ExceptionalAttributes } from './vital-sign-create-and-edit-service';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { VitalSignFields, VitalSignFieldsMap } from './vital-sign-fields-map';
import { YesNoType } from '../../../utils/enum-utils';
import { DateFormatterUtil, NumericFormatterUtil } from '@philips-emr/health-professional-components/src/utils';
import { FoundationUtils } from '../../../utils/foundation-utils';
import { FunctionName, PEP_281Parameters } from '../../../utils/parameters';
import ValidateTypeScale from '../services/validate-type-scale';

import './vital-sign-create-and-edit-controller.css';
import { VitalSignRootService } from '../vital-sign-respiratory-monitoring/vital-sign-root-service';

interface VitalSignSubmitProps {
  nmAttribute: string;
  value: string | number | undefined;
}

interface VitalSignValuesProps {
  [key: string]: string | number | moment.Moment;
}

interface AttributesProps {
  component: Metadata;
  values: VitalSignValuesProps;
}

export interface CdScalePain {
  label: string;
  value: string;
}

export interface DomainValue {
  cd: string;
  ds: string;
}
interface TimeValue {
  currentDateTime: string;
}

export enum WeightUnit {
  KG = 'Kg',
  G = 'g'
}

export enum HeightUnit {
  CM = 'cm',
  M = 'm'
}

enum TemperatureUnity {
  CELSIUS     = '°C',
  FAHRENHEIT  = '°F'
}

enum DisplayInformation {
  AMBULATORY_VIEW = 'A',
  NO              = 'N',
  YES             = 'S'
}

enum MessageStatus {
  OK = 'N',
  ERROR = 'E',
  WARNING = 'A'
}

@Controller({ component: VitalSignCreateAndEditAliases.LAYOUT, fragment: FragmentNames.VITAL_SIGN_CREATE_AND_EDIT })
class VitalSignCreateAndEditController extends BaseController {

  @Inject(VitalSignCreateAndEditAliases.PANEL_LOADER)
  private panelLoaderApi!: PanelAPI;

  @Inject(VitalSignCreateAndEditAliases.PANEL_SUCCESS_LOADER)
  private panelSuccessLoaderApi!: PanelAPI;

  @Inject(VitalSignCreateAndEditAliases.DYNAMIC_DATA_TIME_FIELD)
  public fieldDateTimeApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.DYNAMIC_DATA_TIME_INPUT)
  private inputDateTimeApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.DELETE_ACTION)
  private actionDeleteApi!: ActionAPI;

  @Inject(VitalSignCreateAndEditAliases.INATIVATE_ACTION)
  private actionInactivateApi!: ActionAPI;

  @Inject(VitalSignCreateAndEditAliases.FORM)
  private formVitalSignApi!: FormAPI;

  @Inject(VitalSignCreateAndEditAliases.RELEASE_DATE_FIELD)
  public fieldReleaseDateApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.RETROACTIVE_PERIOD_FIELD)
  public fieldRetroactivePeriodApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.STATUS_FIELD)
  public fieldStatusApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.PROFESSIONAL_FIELD)
  public fieldProfessionalApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.PASITION_PA_FIELD)
  public fieldPositionPaApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.POSITION_PA_AUTOCOMPLETE)
  private autocompletePositionPaApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.DEVICE_PA_FIELD)
  public fieldDevicePaApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.DEVICE_PA_AUTOCOMPLETE)
  private autocompleteDevicePaApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.CUFF_PA_FIELD)
  public fieldCuffPaApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.CUFF_PA_AUTOCOMPLETE)
  private autocompleteCuffPaApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.MEMBER_PA_FIELD)
    fieldMemberPaApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.MEMBER_PA_AUTOCOMPLETE)
  private autocompleteMemberPaApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.PAS_FIELD)
  public fieldPasApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.PAS_INPUT)
  private inputPasApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.PAD_FIELD)
  public fieldPadApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.PAD_INPUT)
  private inputPadApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.PAM_FIELD)
  public fieldPamApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.PAM_INPUT)
  private inputPamApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.HEART_RATE_FIELD)
  public fieldHeartRateApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.HEART_RATE_INPUT)
  private inputHeartRateApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.RESPIRATORY_FREQUENCY_FIELD)
  public fieldRespiratoryFrequencyApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.RESPIRATORY_FREQUENCY_INPUT)
  private inputRespiratoryFrequencyApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.TEMPERATURE_FIELD)
  public fieldTemperatureApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.TEMPERATURE_INPUT)
  private inputTemperatureApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.PLACE_FIELD)
  public fieldPlaceApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.PLACE_AUTOCOMPLETE)
  private autocompletePlaceApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.CD_PAIN_SCALE_FIELD)
  public fieldCdPainScaleApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.CD_PAIN_SCALE_AUTOCOMPLETE)
  private autocompleteCdPainScaleApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.PAIN_SCALE_FIELD)
  public fieldPainScaleApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.PAIN_SCALE_INPUT)
  private inputPainScaleApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.RESULT_FIELD)
  public fieldResultApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.RESULT_INPUT)
  private inputResultApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.TOPOGRAPHY_FIELD)
  public fieldTopographyApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.TOPOGRAPHY_AUTOCOMPLETE)
  private autocompleteTopographyApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.IE_SIDE_FIELD)
  public fieldIeSIdeApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.IE_SIDE_AUTOCOMPLETE)
  private autocompleteIeSideApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.KIND_OF_PAIN_FIELD)
  public fieldKindOfPainApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.KIND_OF_PAIN_AUTOCOMPLETE)
  private autocompleteKindOfPainApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.PAIN_CONDITION_FIELD)
  public fieldPainConditionApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.PAIN_CONDITION_AUTOCOMPLETE)
  private autocompletePainConditionApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.DURATION_FIELD)
  public fieldDurationApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.DURATION_INPUT)
  private inputDurationApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.TIME_COURSE_FIELD)
  public fieldTimeCourseApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.TIME_COURSE_AUTOCOMPLETE)
  private autocompleteTimeCourseApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.FREQUENCY_FIELD)
  public fieldFrequencyApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.FREQUENCY_AUTOCOMPLETE)
  private autocompleteFrequencyApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.IRRADIATION_TOPOGRAPHY_FIELD)
  public fieldIrradiationTopographyApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.IRRADIATION_TOPOGRAPHY_AUTOCOMPLETE)
  private autocompleteIrradiationTopographyApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.OUTCAST_SIDE_FIELD)
  public fieldOutcastSideApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.OUTCAST_SIDE_AUTOCOMPLETE)
  private autocompleteOutcastSideApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.PAIN_COMFORT_LEVEL_FIELD)
  public fieldPainComfortLevelApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.PAIN_COMFORT_LEVEL_AUTOCOMPLETE)
  private autocompletePainComfortLevelApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.INEPT_REASON_PAIN_FIELD)
  public fieldIneptReasonPainApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.INEPT_REASON_PAIN_AUTOCOMPLETE)
  private autocompleteIneptReasonPainApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.CONDUCT_RECORD_FIELD)
  public fieldConductRecordApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.CONDUCT_RECORD_AUTOCOMPLETE)
  private autocompleteConductRecordApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.SATURATION_O2_FIELD)
  public fieldSaturationO2Api!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.SATURATION_O2_INPUT)
  private inputSaturationO2Api!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.SATURATION_CONDITION_FIELD)
  public fieldSaturationConditionApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.SATURATION_CONDITION_AUTOCOMPLETE)
  private autocompleteSaturationConditionApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.FC_OXI_FIELD)
  public fieldFcOxiApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.FC_OXI_INPUT)
  private inputFcOxiApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.SATURATION_MEMBER_FIELD)
  public fieldSaturationMemberApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.SATURATION_MEMBER_AUTOCOMPLETE)
  private autocompleteSaturationMemberApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.O2_ADDITIONAL_FIELD)
  public fieldO2AdditionalApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.O2_ADDITIONAL_INPUT)
  private inputO2AdditionalApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.UM_ADDITIONAL_FIELD)
  public fieldUmAdditionalApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.UM_ADDITIONAL_AUTOCOMPLETE)
  private autocompleteUmAdditionalApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.BLOOD_GLUCOSE_VARIATION_FIELD)
  public fieldBloodGlucoseVariationApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.BLOOD_GLUCOSE_VARIATION_INPUT)
  private inputBloodGlucoseVariationApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.CAPILLARY_BLOOD_GLUCOSE_FIELD)
  public fieldCapillaryBloodGlucoseApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.CAPILLARY_BLOOD_GLUCOSE_INPUT)
  private inputCapillaryBloodGlucoseApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.BLOOD_GLUCOSE_CONTROL_FIELD)
  public fieldBloodGlucoseControlApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.BLOOD_GLUCOSE_CONTROL_AUTOCOMPLETE)
  private autocompleteBloodGlucoseControlApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.TWENTY_FOUR_HOUR_AVERAGE_FIELD)
  public field24HourAverageApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.TWENTY_FOUR_HOUR_AVERAGE_INPUT)
  private input24HourAverageApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.GLUCOSE_FIELD)
  public fieldGlucoseApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.GLUCOSE_INPUT)
  private inputGlucoseApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.INSULIN_FIELD)
  public fieldInsulinApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.INSULIN_INPUT)
  private inputInsulinApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.ECG_RHYTHM_FIELD)
  public fieldEcgRhythmApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.ECG_RHYTHM_AUTOCOMPLETE)
  private autocompleteEcgRhythmApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.ST_DERIVATION_FIELD)
  public fieldStDerivationApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.ST_DERIVATION_AUTOCOMPLETE)
  private autocompleteStDerivationApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.VARIATION_FIELD)
  public fieldVariationApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.VARIATION_INPUT)
  private inputVariationApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.DECUBITUS_FIELD)
  public fieldDecubitusApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.DECUBITUS_AUTOCOMPLETE)
  private autocompleteDecubitusApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.PVC_CMH2O_FIELD)
  public fieldPvcCmh2oApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.PVC_CMH2O_INPUT)
  private inputPvcCmh2oApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.PVC_MMHF_FIELD)
  public fieldPvcMmhfApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.PVC_MMHF_INPUT)
  private inputPvcMmhfApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.PAE_MMHF_FIELD)
  public fieldPaeMmhfApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.PAE_MMHF_INPUT)
  private inputPaeMmhfApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.PIA_CMH2O_FIELD)
  public fieldPiaCmh2oApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.PIA_CMH2O_INPUT)
  private inputPiaCmh2oApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.PPA_MMHG_FIELD)
  public fieldPpaMmhgApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.PPA_MMHG_INPUT)
  private inputPpaMmhgApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.PIC_MMHG_FIELD)
  public fieldPicMmhgApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.PIC_MMHG_INPUT)
  private inputPicMmhgApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.PIC_CATHETER_TEMPERATURE_FIELD)
  public fieldPicCatheterTemperatureApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.PIC_CATHETER_TEMPERATURE_INPUT)
  private inputPicCatheterTemperatureApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.PPC_MMHG_FIELD)
  public fieldPpcMmhgApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.TOF_FIELD)
  public fieldTofApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.TOF_INPUT)
  private inputTofApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.DEGREES_FIELD)
  public fieldDegreesApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.DEGREES_INPUT)
  private inputDegreesApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.DATE_OF_WEIGHT_FIELD)
  public fieldDateOfWeightApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.DATE_OF_WEIGHT_INPUT)
  private inputDateOfWeightApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.WEIGHT_UNIT_MEASURE_FIELD)
  public fieldWeightUnitMeasureApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.WEIGHT_UNIT_MEASURE_AUTOCOMPLETE)
  private autocompleteWeightUnitMeasureApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.ORIGIN_WIDTH_FIELD)
  public fieldOriginWidthApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.ORIGIN_WIDTH_AUTOCOMPLETE)
  private autocompleteOriginWidthApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.DELTA_WEIGHT_FIELD)
  public fieldDeltaWeightApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.DELTA_WEIGHT_FIELD)
  private inputDeltaWeightApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.HEIGHT_UNIT_MEASURE_FIELD)
  public fieldHeightUnitMeasureApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.HEIGHT_UNIT_MEASURE_AUTOCOMPLETE)
  private autocompleteHeightUnitMeasureApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.HEIGHT_ORIGIN_FIELD)
  public fieldHeightOriginApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.HEIGHT_ORIGIN_AUTOCOMPLETE)
  private autocompleteHeightOriginApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.SC_FIELD)
  public fieldScApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.SC_INPUT)
  private inputScApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.IMC_DESCRIPTION_FIELD)
  public fieldImcDescriptionApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.IMC_DESCRIPTION_INPUT)
  private inputImcDescriptionApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.CALF_CIRCUMFERENCE_FIELD)
  public fieldCalfCircumferenceApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.CALF_CIRCUMFERENCE_INPUT)
  private inputCalfCircumferenceApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.ARM_CIRCUMFERENCE_FIELD)
  public fieldArmCircumferenceApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.ARM_CIRCUMFERENCE_INPUT)
  private inputArmCircumferenceApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.TRIPLE_SKIN_FOLD_FIELD)
  public fieldTripleSkinFoldApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.TRIPLE_SKIN_FOLD_INPUT)
  private inputTripleSkinFoldApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.CMB_FIELD)
  public fieldCmbApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.CMB_INPUT)
  private inputCmbApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.RELATIONSHIP_A_Q_FIELD)
  public fieldRelationshipAQApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.RELATIONSHIP_A_Q_INPUT)
  private inputRelationshipAQApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.CEPHALIC_FIELD)
  public fieldCephalicApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.CEPHALIC_INPUT)
  private inputCephalicApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.THORACIC_FIELD)
  public fieldThoracicApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.THORACIC_INPUT)
  private inputThoracicApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.ABDOMINAL_FIELD)
  public fieldAbdominalApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.ABDOMINAL_INPUT)
  private inputAbdominalApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.HIP_FIELD)
  public fieldHipApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.HIP_INPUT)
  private inputHipApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.INCUBATOR_HUMIDITY_FIELD)
  public fieldIncubatorHumidityApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.INCUBATOR_HUMIDITY_INPUT)
  private inputIncubatorHumidityApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.INCUBATOR_TEMPERATURE_FIELD)
  public fieldIncubatorTemperatureApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.INCUBATOR_TEMPERATURE_INPUT)
  private inputIncubatorTemperatureApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.CRIB_TEMPERATURE_FIELD)
  public fieldCribTemperatureApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.CRIB_TEMPERATURE_INPUT)
  private inputCribTemperatureApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.BTT_FIELD)
  public fieldBttApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.BTT_INPUT)
  private inputBttApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.BTF_FIELD)
  public fieldBtfApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.BTF_INPUT)
  private inputBtfApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.RADIANCE_FIELD)
  public fieldRadianceApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.RADIANCE_INPUT)
  private inputRadianceApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.RIGHT_PULLEY_SIZE_FIELD)
  public fieldRightPulleySizeApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.RIGHT_PULLEY_SIZE_AUTOCOMPLETE)
  private autocompleteRightPulleySizeApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.LEFT_PULLEY_SIZE_FIELD)
  public fieldLeftPulleySizeApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.LEFT_PULLEY_SIZE_AUTOCOMPLETE)
  private autocompleteLeftPulleySizeApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.RIGHT_EYE_LIGHT_REACTION_FIELD)
  public fieldRightEyeLightReactionApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.RIGHT_EYE_LIGHT_REACTION_AUTOCOMPLETE)
  private autocompleteRightEyeLightReactionApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.LEFT_EYE_LIGHT_REACTION_FIELD)
  public fieldLeftEyeLightReactionApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.LEFT_EYE_LIGHT_REACTION_AUTOCOMPLETE)
  private autocompleteLeftEyeLightReactionApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.ALTERATION_FIELD)
  public fieldAlterationApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.ALTERATION_AUTOCOMPLETE)
  private autocompleteAlterationApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.SIDE_PUPIL_FIELD)
  public fieldSidePupilApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.SIDE_PUPIL_AUTOCOMPLETE)
  private autocompleteSidePupilApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.FOCAL_SIGNAL_FIELD)
  public fieldFocalSignalApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.FOCAL_SIGNAL_AUTOCOMPLETE)
  private autocompleteFocalSignalApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.FOCAL_SIGNAL_SPOT_FIELD)
  public fieldFocalSignalSpotApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.FOCAL_SIGNAL_SPOT_AUTOCOMPLETE)
  private autocompleteFocalSignalSpotApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.LEVEL_OF_CONSCIOUSNESS_FIELD)
  public fieldLevelOfConsciousnessApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.LEVEL_OF_CONSCIOUSNESS_AUTOCOMPLETE)
  private autocompleteLevelOfConsciousnessApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.BABINSKI_FIELD)
  public fieldBabinskiApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.BABINSKI_AUTOCOMPLETE)
  private autocompleteBabinskiApi!: AutocompleteAPI;

  @Inject(VitalSignCreateAndEditAliases.OBSTETRIC_RECORDS_FIELD)
  public fieldObstetricRecordsApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.OBSTETRIC_RECORDS_INPUT)
  private inputObstetricRecordsApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.OBSTETRIC_RECORDS_BCF2_FIELD)
  public fieldObstetricRecordsBcf2Api!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.OBSTETRIC_RECORDS_BCF2_INPUT)
  private inputObstetricRecordsBcf2Api!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.OBSTETRIC_RECORDS_BCF3_FIELD)
    fieldObstetricRecordsBcf3Api!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.OBSTETRIC_RECORDS_BCF3_INPUT)
  private inputObstetricRecordsBcf3Api!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.OBSTETRIC_RECORDS_OBSERVATION_FIELD)
  public fieldObstetricRecordsObservationApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.OBSTETRIC_RECORDS_OBSERVATION_TEXTAREA)
  private textAreaObstetricRecordsObservationApi!: TextAreaAPI;

  @Inject(VitalSignCreateAndEditAliases.KG_WEIGHT_FIELD)
  public fieldKgWeightApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.KG_WEIGHT_INPUT)
  private inputKgWeightApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.G_WEIGHT_FIELD)
  public fieldGweightApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.G_WEIGHT_INPUT)
  private inputGweightApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.HEIGHT_CENTIMETERS_FIELD)
  public fieldHeightCentimetersApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.HEIGHT_CENTIMETERS_INPUT)
  private inputHeightCentimetersApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.HEIGHT_METERS_FIELD)
  public fieldHeightMetersApi!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.HEIGHT_METERS_INPUT)
  private inputHeightMetersApi!: InputAPI;

  @Inject(VitalSignCreateAndEditAliases.TEMPERATURE_UNITY_FIELD)
  public temperatureUnityFieldAPI!: FieldAPI;

  @Inject(VitalSignCreateAndEditAliases.TEMPERATURE_UNITY_AUTOCOMPLETE)
  private autocompleteTemperatureUnityAPI!: AutocompleteAPI;

  setVisibleFieldAPI(customApi: AutocompleteAPI | TextAreaAPI | null, fieldApi: FieldAPI, fieldEntry: string, input: InputAPI | null): void {
    const { isDetails } = this.historyService.getCurrentRouteParams();
    const isDetailsScreen = isDetails === YesNoType.YES;

    const fieldMap = this.context.get('fieldMap') as Record<string, VitalSignFields>;
    if (!fieldMap) {
      return
    }

    const field = fieldMap[fieldEntry]?.metadata as Metadata;
    if (!field) {
      return
    }

    const fieldLabel = field.label as string;

    if (customApi instanceof AutocompleteAPI) {
      customApi?.setData(field.options as Record<string, Value>[]);
    }

    const fieldFormatterType = input?.getFormatters()?.value?.name
    fieldApi.setLabel(fieldLabel);
    fieldApi.setValidators([
      { name: field?.required === true && !isDetailsScreen ? 'required' : '' },
      { name: customApi || fieldFormatterType === 'date' ? '' : 'numeric' }
    ]);
    customApi?.setEnabled(true);
    customApi?.setReadOnly(isDetailsScreen);
    input?.setReadOnly(isDetailsScreen);
  }

  private convertCelsius(isOldCelsius: boolean, currentTemp: number): void {
    if (isOldCelsius) {
      this.fieldTemperatureApi.setValue(currentTemp);
      return;
    }
    const newTemperature = (currentTemp - 32) * 5/9;
    this.fieldTemperatureApi.setValue(newTemperature);
  }

  private convertFahrenheit(isOldFahrenheit: boolean, currentTemp: number): void {
    if (isOldFahrenheit) {
      this.fieldTemperatureApi.setValue(currentTemp);
      return;
    }
    const newTemperature = (currentTemp * 9/5) + 32;
    this.fieldTemperatureApi.setValue(newTemperature);
  }

  private handleTemperatureUnityField(event: ChangeEvent<FieldAPI>): void {
    const newValue = event.prettyValue as unknown as string;
    const oldValue = event?.oldValue as unknown as DomainValue;
    const isOldCelsius = oldValue.ds === TemperatureUnity.CELSIUS;
    const isOldFahrenheit = oldValue.ds === TemperatureUnity.FAHRENHEIT;
    const currentTemp = this.fieldTemperatureApi.getValue() as number;

    if (newValue === TemperatureUnity.CELSIUS) {
      this.convertCelsius(isOldCelsius, currentTemp);
    }

    if (newValue === TemperatureUnity.FAHRENHEIT) {
      this.convertFahrenheit(isOldFahrenheit, currentTemp);
    }

  }
  validateWhichFieldsAreVisible(metadata: Metadata): void {
    const { isDetails } = this.historyService.getCurrentRouteParams();
    const isDetailsScreen = isDetails === YesNoType.YES
    const fieldMap = VitalSignFieldsMap.makeData(this);
    const checkEnabled = this.getParamAccess(FunctionName.PEP_281, PEP_281Parameters.DISPLAY_INFORMATION_VITAL_SIGNS_PA_SCREENING);
    const unsupportedFieldsMessage = MessageService.translate('text.fields-not-supported');
    const supportedFields: string[] = [];
    const unsupportedFields: string[] = [];

    metadata?.children?.forEach((fieldSet) => {
      fieldSet?.children?.forEach((field) => {
        const fieldEntry = field.entry as string;
        const fieldLabel = field.label as string;

        if (fieldMap[fieldEntry]) {
          fieldMap[fieldEntry] = { ...fieldMap[fieldEntry], metadata: field };
          fieldMap[fieldEntry].field.setVisible(true);
          supportedFields.push(fieldLabel);
        } else {
          unsupportedFields.push(fieldLabel);
        }

      });
    });

    if (unsupportedFields.length > 0) {
      ShowToast.open(unsupportedFieldsMessage.replace('#@UNSUPPORTED_FIELDS#@', unsupportedFields.join(', ')), ToastLevel.ERROR);

      if (supportedFields.length === 0) {
        applicationContext.popNavigationItem();
        return;
      }
    }

    this.context.set({ fieldMap });

    this.fieldDateTimeApi.setVisible(true);
    this.fieldDateTimeApi.setValidators([{ name: !isDetailsScreen ? 'required' : '' }]);
    this.fieldReleaseDateApi.setVisible(isDetailsScreen);
    this.fieldRetroactivePeriodApi.setVisible(isDetailsScreen);
    this.fieldStatusApi.setVisible(isDetailsScreen);
    this.fieldProfessionalApi.setVisible(isDetailsScreen);
    this.temperatureUnityFieldAPI.setVisible(checkEnabled === DisplayInformation.AMBULATORY_VIEW && !isDetailsScreen);
  }

  validateConfiguredFields(metadata: Metadata | null): boolean {
    const noFieldsMessage = MessageService.translate('text.no-fields-configured');

    if (!metadata) {
      ShowToast.open(noFieldsMessage, ToastLevel.ERROR);
      applicationContext.popNavigationItem();
      return false;
    }

    return true;
  }

  filtersFieldsWithValuesVisibleInTheDetailsScreen(components: Metadata, values: VitalSignValuesProps): void {
    components.children.forEach((fieldSet) => {
      fieldSet.children = fieldSet.children?.filter((field) => {
        const fieldEntry = field.entry as string;
        return values[fieldEntry] !== undefined && field;
      });

      return fieldSet.children;
    });
  }

  private formatDecimalValuesForDisplay(values: VitalSignValuesProps): void {
    NumericFormatterUtil.formatDecimalValues(values);
  }

  detailsVitalSignScreen(): void {
    const { encounterId, patientId, vitalSignId } = this.historyService.getCurrentRouteParams();

    HttpService.get(Endpoint.GET_VITAL_SIGN, { encounterId, patientId, vitalSignId })
      .then((response) => {
        const responseBody = response.body as Record<string, Value>;
        const user = foundationContext.getUser() as HealthProfessionalUser;
        const dynamicForm = responseBody.dynamicForm as unknown as AttributesProps;
        const vitalSign = responseBody.vitalSign as unknown as Record<string, Value>;
        const dtSinalVital = responseBody.dtSinalVital as string;
        const allowInfo = InformationBarUtils.allowInfo && VitalSignRootService.isAllowEdit;
        const showInactivate = vitalSign.professionalCode === user.physicalPersonId && Boolean(responseBody.showInactivate) && allowInfo;
        const validateFields = this.validateConfiguredFields(dynamicForm.component);

        if (!validateFields) {
          return;
        }

        this.formatDecimalValuesForDisplay(dynamicForm.values);

        this.context.set({ dtSinalVital: dtSinalVital, responseBodyValues: dynamicForm.values });

        this.filtersFieldsWithValuesVisibleInTheDetailsScreen(dynamicForm.component, dynamicForm.values);
        this.actionInactivateApi.setEnabled(showInactivate);

        dynamicForm.values.RELEASE_DATE = DateFormatterUtil.timeStamp(String(vitalSign?.dtLiberacao));
        dynamicForm.values.RETROACTIVE_PERIOD = vitalSign?.iePeriodoRetroativo === YesNoType.YES ? MessageService.translate('label.status-yes') : MessageService.translate('label.status-no');
        dynamicForm.values.STATUS = vitalSign?.ieStatus === 'A' ? MessageService.translate('value-description.vital-sign.status.active') : MessageService.translate('tws.text-legend-pending');
        dynamicForm.values.PROFESSIONAL = String(vitalSign?.professionalName);

        this.validateWhichFieldsAreVisible(dynamicForm.component);
        this.panelLoaderApi.setVisible(false);
      });
  }

  getCurrentDate(): Promise<HttpResponse<TimeValue>> {
    return HttpService.get<TimeValue>(Endpoint.GET_CURRENT_DATE_TIME);
  }

  createVitalSignScreen(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();

    Promise.all([
      HttpService.get(Endpoint.VITAL_SIGN_GET_ATTRIBUTES, { encounterId, patientId }),
      this.getCurrentDate()
    ]).then(response => {
      const responseBody = response[0].body as AttributesProps;
      const currentDate = response[1].body?.currentDateTime;
      const validateFields = this.validateConfiguredFields(responseBody.component);

      if (!validateFields) {
        return;
      }

      this.context.set({ dtSinalVital:  currentDate, responseBodyComponent: responseBody.component, responseBodyValues: responseBody.values });

      this.validateWhichFieldsAreVisible(responseBody.component);
      this.panelLoaderApi.setVisible(false);
    });

  }

  editVitalSignScreen(): void {
    const { encounterId, patientId, vitalSignId } = this.historyService.getCurrentRouteParams();

    HttpService.get(Endpoint.GET_VITAL_SIGN, { encounterId, patientId, vitalSignId })
      .then((response) => {
        const responseBody = response.body as Record<string, Value>;
        const dynamicForm = responseBody.dynamicForm as unknown as AttributesProps;
        const dtSinalVital = responseBody.dtSinalVital as string;
        const validateFields = this.validateConfiguredFields(dynamicForm.component);

        if (!validateFields) {
          return;
        }

        this.context.set({ dtSinalVital: dtSinalVital, responseBodyComponent: dynamicForm.component, responseBodyValues: dynamicForm.values });

        this.validateWhichFieldsAreVisible(dynamicForm.component);
        this.panelLoaderApi.setVisible(false);
      });
  }

  inativateVitalSignModal(): void {
    const { patientId, encounterId, vitalSignId } = this.historyService.getCurrentRouteParams();

    const dialogModal: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      contextData: {
        encounterId: encounterId as string,
        patientId: patientId as string,
        vitalSignId: vitalSignId as string,
      },
      focusOnOpen: false,
      fragment: 'vital-sign-inactivate',
      id: 'vital-sign-inactivate',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'title.disable-record',
      uuid: generateUUID(),
    };

    ModalService.openDefault(dialogModal);
  }

  deleteVitalSign(): void {
    const { encounterId, vitalSignId } = this.historyService.getCurrentRouteParams();

    VitalSignService.delete(Number(encounterId), Number(vitalSignId))
      .then(() => {
        applicationContext.popNavigationItem();
      });
  }

  deleteVitalSignDialog(): void {
    DialogService.open({
      descriptionDialog: 'vita-sign.title-delete',
      onClickCancel: () => {},
      onClickOk: () => {this.deleteVitalSign();},
      titleDialog: 'vita-sign.title-delete',
      titleOnCancel: 'sign-vital.btn-not-now',
      titleOnOk: 'vital-sign.btn-delete'
    });
  }

  discardChanges(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  discardChangesDialog(): void {
    DialogService.open({
      descriptionDialog: 'clinical-notes.discard-changes',
      onClickCancel: () => {},
      onClickOk: () => {this.discardChanges();},
      titleDialog: 'clinical-notes.discard-changes',
      titleOnCancel: 'patient-evolution.cancel',
      titleOnOk: 'clinical-notes.discard-submit'
    });
  }

  discardRelease(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  releaseVitalSign(vitalSignalId: number): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

    HttpService.put(Endpoint.VITAL_SIGN_RELEASE, { encounterId, patientId, vitalSignalId })
      .then(() => {
        applicationContext.popNavigationItem();
      }).catch(error => {
        ShowToast.open(error?.body.errorMessage, ToastLevel.ERROR);
      })
  }

  releaseDialog(vitalSignalId: number): void {
    DialogService.open({
      descriptionDialog: 'vital-sign.release-submit.text-subtitle',
      onClickCancel: () => {this.discardRelease();},
      onClickOk: () => {this.releaseVitalSign(vitalSignalId);},
      titleDialog: 'vital-sign.release-submit-text-title',
      titleOnCancel: 'vital-sign.release-bnt-cancel',
      titleOnOk: 'vital-sign.release-btn-submit'
    });
  }

  async submitFormData(body: Record<string, Value>): Promise<boolean> {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

    return HttpService.post(Endpoint.VITAL_SIGN, { encounterId, patientId }, { body })
      .then((response)=> {
        const vitalSignalId = response.body as number;
        this.context.set({ vitalSignalId });

        this.releaseDialog(vitalSignalId);

        return false;
      }).catch((error) => {
        this.formDisabled(false);
        error?.body.errorMessage && ShowToast.open(error?.body.errorMessage, ToastLevel.ERROR);
        return true;
      }).finally(() => this.panelSuccessLoaderApi.setVisible(false));
  }

  createFormInfos(event: AbortableSubmitEvent): VitalSignSubmitProps[] {
    const keys = Object.keys(event.record);
    const values = Object.values(event.record);

    return keys.map((key, index) => {
      if (typeof values[index] === 'object') {
        return  {
          nmAttribute: [key][0],
          value: values[index].value
        };
      }

      return {
        nmAttribute: [key][0],
        value: values[index]
      };
    }).filter(this.removeExceptionalAttributes);
  }

  private removeExceptionalAttributes(item: VitalSignSubmitProps): boolean {
    return !Object.keys(ExceptionalAttributes).includes(item.nmAttribute);
  }

  findAttribute(formInfos: VitalSignSubmitProps[], attributeName: string): string | number | undefined {
    const attribute =  formInfos.find((item) => item.nmAttribute === attributeName);
    return attribute?.value;
  }

  validateInputs(message: string, messageType: string | undefined, value: number | undefined, field: FieldAPI): void {
    const validatorName = 'outOfRangerValidator';

    if (value === undefined || value === null || value === 0) {
      message = '';
      messageType = MessageStatus.OK;
    }

    field.setValidators([{ name: validatorName, options: {
      message,
      status: messageType,
      value
    }}]);

    this.formVitalSignApi.validate();
  }

  validateFieldOnBlur(vitalSignAttribute: VitalSignAttributes, value: number, fieldApi: FieldAPI): void {
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const { patientId } = this.context.getBulkData();
    VitalSignService.validateVitalSign(
      Number(encounterId),
      vitalSignAttribute,
      Number(patientId),
      value
    ).then((response) => {
      this.validateInputs(response?.message, response?.ieMessage, value, fieldApi);
    });
  }

  private calculateMAP(): void {
    const qtPASistolica     = this.fieldPasApi.getValue();
    const qtPADistolica     = this.fieldPadApi.getValue();

    const checkPAS          = qtPASistolica != null && qtPASistolica != '';
    const checkPAD          = qtPADistolica != null && qtPADistolica != '';
    const checkForNewValue  = checkPAS && checkPAD;

    this.fieldPamApi.setValue('');

    if (checkForNewValue) {
      const newValue = ((Number(qtPASistolica) + (Number(qtPADistolica) * 2)) / 3)
      this.fieldPamApi.setValue(Math.trunc(newValue));
      this.validateFieldOnBlur(VitalSignAttributes.QT_PAM, newValue, this.fieldPamApi)
    }

    this.calculatePPC();
  }

  private calculatePPC(): void {
    const qtPAM = this.fieldPamApi.getValue();
    const qtPIC = this.fieldPicMmhgApi.getValue();

    const checkPAM = qtPAM != null && qtPAM != '';
    const checkPIC = qtPIC != null && qtPIC != '';
    const checkForNewValue = checkPAM && checkPIC;

    this.fieldPpcMmhgApi.setValue('');

    if (checkForNewValue) {
      const newValue = Number(qtPAM) - Number(qtPIC);
      this.fieldPpcMmhgApi.setValue(Math.trunc(newValue));
    }
  }

  private getValueWithoutComma(fieldApi: FieldAPI): number {
    const result = Number(String(fieldApi.getValue()).replace(',' , '.'))
    return Number(result.toFixed(2))
  }

  private unitSelected(inputValue: string | Record<string, string> | undefined, measureUnit: string): boolean {
    return inputValue === measureUnit && inputValue != null;
  }

  private handleAutocompleteHeightChange(event: ChangeEvent<FieldAPI>, fieldCentimetersApi: FieldAPI, fieldMetersApi: FieldAPI): void {
    const eventValue          = event.value as unknown as Record<string, string>;
    const inputValue          = event.prettyValue ? event.prettyValue : eventValue.value;
    const hasOldValue         = JSON.stringify(event.oldValue);
    const centimetersSelected = this.unitSelected(inputValue, HeightUnit.CM)
    const metersSelected      = this.unitSelected(inputValue, HeightUnit.M)
    const metersFilled        = this.getValueWithoutComma(fieldMetersApi);
    const centimetersFilled   = this.getValueWithoutComma(fieldCentimetersApi);

    this.context.set({ heightUnitValue: inputValue });

    if(metersSelected && centimetersFilled && hasOldValue) {
      fieldMetersApi.setValue((centimetersFilled / 100).toFixed(2));
      fieldCentimetersApi.setValue(undefined);
    }

    if(centimetersSelected && metersFilled && hasOldValue) {
      fieldCentimetersApi.setValue(metersFilled * 100)
      fieldMetersApi.setValue('')
    }

    if(inputValue == '') {
      fieldCentimetersApi.setValue('');
      fieldMetersApi.setValue('')
    }

    fieldMetersApi.setVisible(metersSelected);
    fieldCentimetersApi.setVisible(centimetersSelected);
  }

  private getParamAccess(module: string, parameter: string): string {
    return FoundationUtils.findModuleParam(module, parameter) as string;
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.LAYOUT })
  onMountLayout(): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    InformationBarUtils.collapsedBar = true;
    this.context.set({
      encounterId: encounterId,
      patientId: patientId,
    });
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.DELETE_ACTION })
  onMountActionDelete(): void {
    const { vitalSignId, isDetails } = this.historyService.getCurrentRouteParams();

    if (vitalSignId && isDetails !== YesNoType.YES) {
      this.actionDeleteApi.setEnabled(true);
    }
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.FORM })
  onMountForm(): void {
    const { isDetails, vitalSignId } = this.historyService.getCurrentRouteParams();

    this.panelLoaderApi.setVisible(true);

    if (isDetails === YesNoType.YES) {
      this.formDisabled(true);
      this.detailsVitalSignScreen();
      return;
    }

    if (vitalSignId) {
      this.editVitalSignScreen();
      return;
    }

    this.createVitalSignScreen();
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.POSITION_PA_AUTOCOMPLETE })
  onMountIePressao(): void {
    this.setVisibleFieldAPI(this.autocompletePositionPaApi, this.fieldPositionPaApi, this.fieldPositionPaApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.DEVICE_PA_AUTOCOMPLETE })
  onMountIeAparelhoPa(): void {
    this.setVisibleFieldAPI(this.autocompleteDevicePaApi, this.fieldDevicePaApi, this.fieldDevicePaApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.CUFF_PA_AUTOCOMPLETE })
  onMountIeManguito(): void {
    this.setVisibleFieldAPI(this.autocompleteCuffPaApi, this.fieldCuffPaApi, this.fieldCuffPaApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.MEMBER_PA_AUTOCOMPLETE })
  onMountIeMembro(): void {
    this.setVisibleFieldAPI(this.autocompleteMemberPaApi, this.fieldMemberPaApi, this.fieldMemberPaApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.PAS_INPUT })
  onMountQtPaSistolica(): void {
    this.setVisibleFieldAPI(null, this.fieldPasApi, this.fieldPasApi.getEntry(), this.inputPasApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.PAD_INPUT })
  onMountQtPaDiastolica(): void {
    this.setVisibleFieldAPI(null, this.fieldPadApi, this.fieldPadApi.getEntry(), this.inputPadApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.PAM_INPUT })
  onMountQtPam(): void {
    this.setVisibleFieldAPI(null, this.fieldPamApi, this.fieldPamApi.getEntry(), this.inputPamApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.HEART_RATE_INPUT })
  onMountQtFreqCardiaca(): void {
    this.setVisibleFieldAPI(null, this.fieldHeartRateApi, this.fieldHeartRateApi.getEntry(), this.inputHeartRateApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.RESPIRATORY_FREQUENCY_INPUT })
  onMountQtFreqResp(): void {
    this.setVisibleFieldAPI(null, this.fieldRespiratoryFrequencyApi, this.fieldRespiratoryFrequencyApi.getEntry(), this.inputRespiratoryFrequencyApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.TEMPERATURE_INPUT })
  onMountQtTemp(): void {
    this.setVisibleFieldAPI(null, this.fieldTemperatureApi, this.fieldTemperatureApi.getEntry(), this.inputTemperatureApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.PLACE_AUTOCOMPLETE })
  onMountIeSitio(): void {
    this.setVisibleFieldAPI(this.autocompletePlaceApi, this.fieldPlaceApi, this.fieldPlaceApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.CD_PAIN_SCALE_AUTOCOMPLETE })
  onMountCdEscalaDor(): void {
    this.setVisibleFieldAPI(this.autocompleteCdPainScaleApi, this.fieldCdPainScaleApi, this.fieldCdPainScaleApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.PAIN_SCALE_INPUT })
  onMountQtEscalaDor(): void {
    this.setVisibleFieldAPI(null, this.fieldPainScaleApi, this.fieldPainScaleApi.getEntry(), this.inputPainScaleApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.RESULT_INPUT })
  onMountNrSeqResultDor(): void {
    this.setVisibleFieldAPI(null, this.fieldResultApi, this.fieldResultApi.getEntry(), this.inputResultApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.TOPOGRAPHY_AUTOCOMPLETE })
  onMountNrSeqTopografiaDor(): void {
    this.setVisibleFieldAPI(this.autocompleteTopographyApi, this.fieldTopographyApi, this.fieldTopographyApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.IE_SIDE_AUTOCOMPLETE })
  onMountIeLado(): void {
    this.setVisibleFieldAPI(this.autocompleteIeSideApi, this.fieldIeSIdeApi, this.fieldIeSIdeApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.KIND_OF_PAIN_AUTOCOMPLETE })
  onMountIeTipoDor(): void {
    this.setVisibleFieldAPI(this.autocompleteKindOfPainApi, this.fieldKindOfPainApi, this.fieldKindOfPainApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.PAIN_CONDITION_AUTOCOMPLETE })
  onMountNrSeqCondicaoDor(): void {
    this.setVisibleFieldAPI(this.autocompletePainConditionApi, this.fieldPainConditionApi, this.fieldPainConditionApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.DURATION_INPUT })
  onMountQtPeriodo(): void {
    this.setVisibleFieldAPI(null, this.fieldDurationApi, this.fieldDurationApi.getEntry(), this.inputDurationApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.TIME_COURSE_AUTOCOMPLETE })
  onMountIePeriodo(): void {
    this.setVisibleFieldAPI(this.autocompleteTimeCourseApi, this.fieldTimeCourseApi, this.fieldTimeCourseApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.FREQUENCY_AUTOCOMPLETE })
  onMountIeFrequenciaDor(): void {
    this.setVisibleFieldAPI(this.autocompleteFrequencyApi, this.fieldFrequencyApi, this.fieldFrequencyApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.IRRADIATION_TOPOGRAPHY_AUTOCOMPLETE })
  onMountNrSeqTopDorIrradia(): void {
    this.setVisibleFieldAPI(this.autocompleteIrradiationTopographyApi, this.fieldIrradiationTopographyApi, this.fieldIrradiationTopographyApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.OUTCAST_SIDE_AUTOCOMPLETE })
  onMountIeLadoDor(): void {
    this.setVisibleFieldAPI(this.autocompleteOutcastSideApi, this.fieldOutcastSideApi, this.fieldOutcastSideApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.PAIN_COMFORT_LEVEL_AUTOCOMPLETE })
  onMountIeNivelConfDor(): void {
    this.setVisibleFieldAPI(this.autocompletePainComfortLevelApi, this.fieldPainComfortLevelApi, this.fieldPainComfortLevelApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.INEPT_REASON_PAIN_AUTOCOMPLETE })
  onMountNrSeqInaptoDor(): void {
    this.setVisibleFieldAPI(this.autocompleteIneptReasonPainApi, this.fieldIneptReasonPainApi, this.fieldIneptReasonPainApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.CONDUCT_RECORD_AUTOCOMPLETE })
  onMountNrSeqConduta(): void {
    this.setVisibleFieldAPI(this.autocompleteConductRecordApi, this.fieldConductRecordApi, this.fieldConductRecordApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.SATURATION_O2_INPUT })
  onMountQtSaturacaoO2(): void {
    this.setVisibleFieldAPI(null, this.fieldSaturationO2Api, this.fieldSaturationO2Api.getEntry(), this.inputSaturationO2Api);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.SATURATION_CONDITION_AUTOCOMPLETE })
  onMountEeCondSatO2(): void {
    this.setVisibleFieldAPI(this.autocompleteSaturationConditionApi, this.fieldSaturationConditionApi, this.fieldSaturationConditionApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.FC_OXI_INPUT })
  onMountQtSpO2R(): void {
    this.setVisibleFieldAPI(null, this.fieldFcOxiApi, this.fieldFcOxiApi.getEntry(), this.inputFcOxiApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.SATURATION_MEMBER_AUTOCOMPLETE })
  onMountIeMembroSatO2(): void {
    this.setVisibleFieldAPI(this.autocompleteSaturationMemberApi, this.fieldSaturationMemberApi, this.fieldSaturationMemberApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.O2_ADDITIONAL_INPUT })
  onMountQtO2Suplementar(): void {
    this.setVisibleFieldAPI(null, this.fieldO2AdditionalApi, this.fieldO2AdditionalApi.getEntry(), this.inputO2AdditionalApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.UM_ADDITIONAL_AUTOCOMPLETE })
  onMountIeUnidMedO2Suplem(): void {
    this.setVisibleFieldAPI(this.autocompleteUmAdditionalApi, this.fieldUmAdditionalApi, this.fieldUmAdditionalApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.BLOOD_GLUCOSE_VARIATION_INPUT })
  onMountQtVariacaoGlic(): void {
    this.setVisibleFieldAPI(null, this.fieldBloodGlucoseVariationApi, this.fieldBloodGlucoseVariationApi.getEntry(), this.inputBloodGlucoseVariationApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.CAPILLARY_BLOOD_GLUCOSE_INPUT })
  onMountQtGlicemiaCapilar(): void {
    this.setVisibleFieldAPI(null, this.fieldCapillaryBloodGlucoseApi, this.fieldCapillaryBloodGlucoseApi.getEntry(), this.inputCapillaryBloodGlucoseApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.BLOOD_GLUCOSE_CONTROL_AUTOCOMPLETE })
  onMountIeGlicExtrapol(): void {
    this.setVisibleFieldAPI(this.autocompleteBloodGlucoseControlApi, this.fieldBloodGlucoseControlApi, this.fieldBloodGlucoseControlApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.TWENTY_FOUR_HOUR_AVERAGE_INPUT })
  onMountQtGlicMediaDia(): void {
    this.setVisibleFieldAPI(null, this.field24HourAverageApi, this.field24HourAverageApi.getEntry(), this.input24HourAverageApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.GLUCOSE_INPUT })
  onMountQtGlicoseAdm(): void {
    this.setVisibleFieldAPI(null, this.fieldGlucoseApi, this.fieldGlucoseApi.getEntry(), this.inputGlucoseApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.INSULIN_INPUT })
  onMountQtInsulina(): void {
    this.setVisibleFieldAPI(null, this.fieldInsulinApi, this.fieldInsulinApi.getEntry(), this.inputInsulinApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.ECG_RHYTHM_AUTOCOMPLETE })
  onMountIeRitmoEcg(): void {
    this.setVisibleFieldAPI(this.autocompleteEcgRhythmApi, this.fieldEcgRhythmApi, this.fieldEcgRhythmApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.ST_DERIVATION_AUTOCOMPLETE })
  onMountIeDerivacaoSegSt(): void {
    this.setVisibleFieldAPI(this.autocompleteStDerivationApi, this.fieldStDerivationApi, this.fieldStDerivationApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.VARIATION_INPUT })
  onMountQtSegmentoSt(): void {
    this.setVisibleFieldAPI(null, this.fieldVariationApi, this.fieldVariationApi.getEntry(), this.inputVariationApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.DECUBITUS_AUTOCOMPLETE })
  onMountEeDecubito(): void {
    this.setVisibleFieldAPI(this.autocompleteDecubitusApi, this.fieldDecubitusApi, this.fieldDecubitusApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.PVC_CMH2O_INPUT })
  onMountQtPvcH2O(): void {
    this.setVisibleFieldAPI(null, this.fieldPvcCmh2oApi, this.fieldPvcCmh2oApi.getEntry(), this.inputPvcCmh2oApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.PVC_MMHF_INPUT })
  onMountQtPvc(): void {
    this.setVisibleFieldAPI(null, this.fieldPvcMmhfApi, this.fieldPvcMmhfApi.getEntry(), this.inputPvcMmhfApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.PAE_MMHF_INPUT })
  onMountQtPae(): void {
    this.setVisibleFieldAPI(null, this.fieldPaeMmhfApi, this.fieldPaeMmhfApi.getEntry(), this.inputPaeMmhfApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.PIA_CMH2O_INPUT })
  onMountQtPressaoIntraAbdH2O(): void {
    this.setVisibleFieldAPI(null, this.fieldPiaCmh2oApi, this.fieldPiaCmh2oApi.getEntry(), this.inputPiaCmh2oApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.PPA_MMHG_INPUT })
  onMountQtPpa(): void {
    this.setVisibleFieldAPI(null, this.fieldPpaMmhgApi, this.fieldPpaMmhgApi.getEntry(), this.inputPpaMmhgApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.PIC_MMHG_INPUT })
  onMountQtPressaoIntraCranio(): void {
    this.setVisibleFieldAPI(null, this.fieldPicMmhgApi, this.fieldPicMmhgApi.getEntry(), this.inputPicMmhgApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.PIC_CATHETER_TEMPERATURE_INPUT })
  onMountQtPicTemp(): void {
    this.setVisibleFieldAPI(null, this.fieldPicCatheterTemperatureApi, this.fieldPicCatheterTemperatureApi.getEntry(), this.inputPicCatheterTemperatureApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.PPC_MMHG_INPUT })
  onMountQtPpc(): void {
    this.setVisibleFieldAPI(null, this.fieldPpcMmhgApi, this.fieldPpcMmhgApi.getEntry(), this.inputPicMmhgApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.TOF_INPUT })
  onMountQtTofBloqNeuroMusc(): void {
    this.setVisibleFieldAPI(null, this.fieldTofApi, this.fieldTofApi.getEntry(), this.inputTofApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.DEGREES_INPUT })
  onMountQtAnguloCabeceira(): void {
    this.setVisibleFieldAPI(null, this.fieldDegreesApi, this.fieldDegreesApi.getEntry(), this.inputDegreesApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.DATE_OF_WEIGHT_INPUT })
  onMountDtDeltaPeso(): void {
    this.setVisibleFieldAPI(null, this.fieldDateOfWeightApi, this.fieldDateOfWeightApi.getEntry(), this.inputDateOfWeightApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.WEIGHT_UNIT_MEASURE_AUTOCOMPLETE })
  onMountIeUnidMedPeso(): void {
    const { isDetails } = this.historyService.getCurrentRouteParams();
    const isDetailsScreen = isDetails === YesNoType.YES;
    const responseBodyValues = this.context.get('responseBodyValues');
    const defaultKgUnit = {
      label: WeightUnit.KG,
      value: WeightUnit.KG
    };
    const defaultWeightUnit = responseBodyValues?.IE_UNID_MED_PESO ? responseBodyValues?.IE_UNID_MED_PESO : defaultKgUnit;


    this.setVisibleFieldAPI(this.autocompleteWeightUnitMeasureApi, this.fieldWeightUnitMeasureApi, this.fieldWeightUnitMeasureApi.getEntry(), null);
    this.fieldWeightUnitMeasureApi.setVisible(!isDetailsScreen);
    this.fieldKgWeightApi.setVisible(!isDetailsScreen);
    this.fieldGweightApi.setVisible(false);
    this.autocompleteWeightUnitMeasureApi.setValue(defaultWeightUnit)
  }

  @OnChange(VitalSignCreateAndEditAliases.TEMPERATURE_UNITY_AUTOCOMPLETE)
  handleChangeTemperatureUnity(event: ChangeEvent<FieldAPI>): void {
    this.handleTemperatureUnityField(event);
  }

  @OnChange({ target: VitalSignCreateAndEditAliases.WEIGHT_UNIT_MEASURE_AUTOCOMPLETE })
  onChangeUnidMedPeso(event: ChangeEvent<FieldAPI>): void {
    this.handleAutocompleteWeightChange(event, this.fieldKgWeightApi, this.fieldGweightApi);
  }
  handleAutocompleteWeightChange(event: ChangeEvent<FieldAPI>, fieldKgWeightApi: FieldAPI, fieldGweightApi: FieldAPI): void {
    const eventValue = event.value as unknown as Record<string, string>;
    const inputValue = event.prettyValue ? event.prettyValue : eventValue.value;
    const hasOldValue = JSON.stringify(event.oldValue)
    const isKilogram = inputValue === WeightUnit.KG && inputValue != null
    const isGram = inputValue === WeightUnit.G && inputValue != null
    const kgFieldValue = Number(fieldKgWeightApi.getValue());
    const gFieldValue = Number(fieldGweightApi.getValue());

    this.context.set({ weightUnitValue: inputValue });

    if (kgFieldValue && isGram && hasOldValue) {
      fieldGweightApi.setValue(kgFieldValue * 1000);
      this.clearField(fieldKgWeightApi)
    }

    if(gFieldValue && isKilogram && hasOldValue) {
      fieldKgWeightApi.setValue(gFieldValue / 1000)
      this.clearField(fieldGweightApi)
    }

    if(inputValue == '') {
      this.clearField(fieldKgWeightApi, fieldGweightApi)
    }

    fieldKgWeightApi.setVisible(isKilogram);
    fieldGweightApi.setVisible(isGram);

  }

  clearField(kg?: FieldAPI, gram?: FieldAPI): void {

    if(kg && gram) {
      kg.setValue('')
      gram.setValue('')
    } else if (kg) {
      kg.setValue('')
    } else if (gram) {
      gram.setValue('')
    }
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.KG_WEIGHT_INPUT})
  onMountKgWeight(): void {
    this.setVisibleFieldAPI(null, this.fieldKgWeightApi, this.fieldKgWeightApi.getEntry(), this.inputKgWeightApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.G_WEIGHT_INPUT})
  onMountGweight(): void {
    this.setVisibleFieldAPI(null, this.fieldGweightApi, this.fieldGweightApi.getEntry(), this.inputGweightApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.ORIGIN_WIDTH_AUTOCOMPLETE })
  onMountIeOrigemPeso(): void {
    this.setVisibleFieldAPI(this.autocompleteOriginWidthApi, this.fieldOriginWidthApi, this.fieldOriginWidthApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.DELTA_WEIGHT_INPUT })
  onMountQtdeltaPeso(): void {
    this.setVisibleFieldAPI(null, this.fieldDeltaWeightApi, this.fieldDeltaWeightApi.getEntry(), this.inputDeltaWeightApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.HEIGHT_UNIT_MEASURE_AUTOCOMPLETE })
  onMountIeUnidMedAltura(): void {
    const { isDetails } = this.historyService.getCurrentRouteParams();
    const isDetailsScreen = isDetails === YesNoType.YES;
    const responseBodyValues = this.context.get('responseBodyValues');
    const defaultMetersUnit = {
      label: HeightUnit.M,
      value: HeightUnit.M
    };
    const defaultHeightUnit = responseBodyValues?.IE_UNID_MED_ALTURA ? responseBodyValues?.IE_UNID_MED_ALTURA : defaultMetersUnit;

    this.setVisibleFieldAPI(this.autocompleteHeightUnitMeasureApi, this.fieldHeightUnitMeasureApi, this.fieldHeightUnitMeasureApi.getEntry(), null);
    this.fieldHeightUnitMeasureApi.setVisible(!isDetailsScreen);
    this.fieldHeightMetersApi.setVisible(!isDetailsScreen);
    this.fieldHeightCentimetersApi.setVisible(false);
    this.autocompleteHeightUnitMeasureApi.setValue(defaultHeightUnit)
  }

  @OnChange({ target: VitalSignCreateAndEditAliases.HEIGHT_UNIT_MEASURE_AUTOCOMPLETE })
  onChangeIeUnidMedAltura(event: ChangeEvent<FieldAPI>): void {
    this.handleAutocompleteHeightChange(event, this.fieldHeightCentimetersApi, this.fieldHeightMetersApi )
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.HEIGHT_CENTIMETERS_INPUT})
  onMountQtAlturaCentimeters(): void {
    this.setVisibleFieldAPI(null, this.fieldHeightCentimetersApi, this.fieldHeightCentimetersApi.getEntry(), this.inputHeightCentimetersApi)
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.HEIGHT_METERS_INPUT})
  onMountQtAlturaMeters(): void {
    this.setVisibleFieldAPI(null, this.fieldHeightMetersApi, this.fieldHeightMetersApi.getEntry(), this.inputHeightMetersApi)
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.HEIGHT_METERS_INPUT})
  onBlurQtAlturaMeters(): void {
    this.fieldHeightMetersApi.setValue(this.getValueWithoutComma(this.fieldHeightMetersApi).toFixed(2))
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.HEIGHT_ORIGIN_AUTOCOMPLETE })
  onMountIeOrigemAltura(): void {
    this.setVisibleFieldAPI(this.autocompleteHeightOriginApi, this.fieldHeightOriginApi, this.fieldHeightOriginApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.SC_INPUT })
  onMountQtSuperfCorporia(): void {
    this.setVisibleFieldAPI(null, this.fieldScApi, this.fieldScApi.getEntry(), this.inputScApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.IMC_DESCRIPTION_INPUT })
  onMountDsImc(): void {
    this.setVisibleFieldAPI(null, this.fieldImcDescriptionApi, this.fieldImcDescriptionApi.getEntry(), this.inputImcDescriptionApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.CALF_CIRCUMFERENCE_INPUT })
  onMountQtCircunfPanturrilha(): void {
    this.setVisibleFieldAPI(null, this.fieldCalfCircumferenceApi, this.fieldCalfCircumferenceApi.getEntry(), this.inputCalfCircumferenceApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.ARM_CIRCUMFERENCE_INPUT })
  onMountQtCircunfBraco(): void {
    this.setVisibleFieldAPI(null, this.fieldArmCircumferenceApi, this.fieldArmCircumferenceApi.getEntry(), this.inputArmCircumferenceApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.TRIPLE_SKIN_FOLD_INPUT })
  onMountQtPregaCutaneaTriceps(): void {
    this.setVisibleFieldAPI(null, this.fieldTripleSkinFoldApi, this.fieldTripleSkinFoldApi.getEntry(), this.inputTripleSkinFoldApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.CMB_INPUT })
  onMountQtCmb(): void {
    this.setVisibleFieldAPI(null, this.fieldCmbApi, this.fieldCmbApi.getEntry(), this.inputCmbApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.RELATIONSHIP_A_Q_INPUT })
  onMountQtRelacaoAbdQuad(): void {
    this.setVisibleFieldAPI(null, this.fieldRelationshipAQApi, this.fieldRelationshipAQApi.getEntry(), this.inputRelationshipAQApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.CEPHALIC_INPUT })
  onMountQtPerimetroCefalico(): void {
    this.setVisibleFieldAPI(null, this.fieldCephalicApi, this.fieldCephalicApi.getEntry(), this.inputCephalicApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.THORACIC_INPUT })
  onMountQtPerimetroToracico(): void {
    this.setVisibleFieldAPI(null, this.fieldThoracicApi, this.fieldThoracicApi.getEntry(), this.inputThoracicApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.ABDOMINAL_INPUT })
  onMountQtPerimetroAbdominal(): void {
    this.setVisibleFieldAPI(null, this.fieldAbdominalApi, this.fieldAbdominalApi.getEntry(), this.inputAbdominalApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.HIP_INPUT })
  onMountQtPerimetroQuadril(): void {
    this.setVisibleFieldAPI(null, this.fieldHipApi, this.fieldHipApi.getEntry(), this.inputHipApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.INCUBATOR_HUMIDITY_INPUT })
  onMountQtUmidIncubadora(): void {
    this.setVisibleFieldAPI(null, this.fieldIncubatorHumidityApi, this.fieldIncubatorHumidityApi.getEntry(), this.inputIncubatorHumidityApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.INCUBATOR_TEMPERATURE_INPUT })
  onMountQtTempIncubadora(): void {
    this.setVisibleFieldAPI(null, this.fieldIncubatorTemperatureApi, this.fieldIncubatorTemperatureApi.getEntry(), this.inputIncubatorTemperatureApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.CRIB_TEMPERATURE_INPUT })
  onMountQtTempBerco(): void {
    this.setVisibleFieldAPI(null, this.fieldCribTemperatureApi, this.fieldCribTemperatureApi.getEntry(), this.inputCribTemperatureApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.BTT_INPUT })
  onMountQtBilirrubinaTronco(): void {
    this.setVisibleFieldAPI(null, this.fieldBttApi, this.fieldBttApi.getEntry(), this.inputBttApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.BTF_INPUT })
  onMountQtBilirrubinaFronte(): void {
    this.setVisibleFieldAPI(null, this.fieldBtfApi, this.fieldBtfApi.getEntry(), this.inputBtfApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.RADIANCE_INPUT })
  onMountQtIrradiancia(): void {
    this.setVisibleFieldAPI(null, this.fieldRadianceApi, this.fieldRadianceApi.getEntry(), this.inputRadianceApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.RIGHT_PULLEY_SIZE_AUTOCOMPLETE })
  onMountQtPupilaTamanho(): void {
    this.setVisibleFieldAPI(this.autocompleteRightPulleySizeApi, this.fieldRightPulleySizeApi, this.fieldRightPulleySizeApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.LEFT_PULLEY_SIZE_AUTOCOMPLETE })
  onMountQtPupilaTamSeq(): void {
    this.setVisibleFieldAPI(this.autocompleteLeftPulleySizeApi, this.fieldLeftPulleySizeApi, this.fieldLeftPulleySizeApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.RIGHT_EYE_LIGHT_REACTION_AUTOCOMPLETE })
  onMountIePupilaReacaoLuz(): void {
    this.setVisibleFieldAPI(this.autocompleteRightEyeLightReactionApi, this.fieldRightEyeLightReactionApi, this.fieldRightEyeLightReactionApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.LEFT_EYE_LIGHT_REACTION_AUTOCOMPLETE })
  onMountIePupilaReacaoLuzE(): void {
    this.setVisibleFieldAPI(this.autocompleteLeftEyeLightReactionApi, this.fieldLeftEyeLightReactionApi, this.fieldLeftEyeLightReactionApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.ALTERATION_AUTOCOMPLETE })
  onMountIePupilaAlteracao(): void {
    this.setVisibleFieldAPI(this.autocompleteAlterationApi, this.fieldAlterationApi, this.fieldAlterationApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.SIDE_PUPIL_AUTOCOMPLETE })
  onMountIePupilaLado(): void {
    this.setVisibleFieldAPI(this.autocompleteSidePupilApi, this.fieldSidePupilApi, this.fieldSidePupilApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.FOCAL_SIGNAL_AUTOCOMPLETE })
  onMountIeSinalFocal(): void {
    this.setVisibleFieldAPI(this.autocompleteFocalSignalApi, this.fieldFocalSignalApi, this.fieldFocalSignalApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.FOCAL_SIGNAL_SPOT_AUTOCOMPLETE })
  onMountIeSinalFocalLocal(): void {
    this.setVisibleFieldAPI(this.autocompleteFocalSignalSpotApi, this.fieldFocalSignalSpotApi, this.fieldFocalSignalSpotApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.LEVEL_OF_CONSCIOUSNESS_AUTOCOMPLETE })
  onMountIeNivelConsciencia(): void {
    this.setVisibleFieldAPI(this.autocompleteLevelOfConsciousnessApi, this.fieldLevelOfConsciousnessApi, this.fieldLevelOfConsciousnessApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.BABINSKI_AUTOCOMPLETE })
  onMountEeBabinski(): void {
    this.setVisibleFieldAPI(this.autocompleteBabinskiApi, this.fieldBabinskiApi, this.fieldBabinskiApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.OBSTETRIC_RECORDS_INPUT })
  onMountQtBcf(): void {
    this.setVisibleFieldAPI(null, this.fieldObstetricRecordsApi, this.fieldObstetricRecordsApi.getEntry(), this.inputObstetricRecordsApi);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.OBSTETRIC_RECORDS_BCF2_INPUT })
  onMountQtBcf2(): void {
    this.setVisibleFieldAPI(null, this.fieldObstetricRecordsBcf2Api, this.fieldObstetricRecordsBcf2Api.getEntry(), this.inputObstetricRecordsBcf2Api);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.OBSTETRIC_RECORDS_BCF3_INPUT })
  onMountQtBcf3(): void {
    this.setVisibleFieldAPI(null, this.fieldObstetricRecordsBcf3Api, this.fieldObstetricRecordsBcf3Api.getEntry(), this.inputObstetricRecordsBcf3Api);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.OBSTETRIC_RECORDS_OBSERVATION_TEXTAREA })
  onMountDsObservacao(): void {
    this.setVisibleFieldAPI(this.textAreaObstetricRecordsObservationApi, this.fieldObstetricRecordsObservationApi, this.fieldObstetricRecordsObservationApi.getEntry(), null);
  }

  @OnMount({ target: VitalSignCreateAndEditAliases.DYNAMIC_DATA_TIME_INPUT })
  onMountDateTime(): void {
    const { isDetails, vitalSignId } = this.historyService.getCurrentRouteParams();
    const isDetailsScreen = isDetails === YesNoType.YES;
    const responseBodyValues = this.context.get('responseBodyValues');
    const dtSinalVital = this.context.get('dtSinalVital');

    this.inputDateTimeApi.setReadOnly(isDetailsScreen);

    if (isDetailsScreen || vitalSignId) {
      this.formVitalSignApi.setValue(responseBodyValues as unknown as Record<string, Value>);
      this.inputDateTimeApi.setValue(dtSinalVital);
      return;
    }

    if (responseBodyValues) {
      responseBodyValues.DT_SINAL_VITAL = dtSinalVital;
      this.formVitalSignApi.setData(responseBodyValues as unknown as Record<string, Value>);
    }

    this.formVitalSignApi.setData({ DT_SINAL_VITAL:  dtSinalVital });
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.PAS_INPUT })
  onBlurPasInput(event: ChangeEvent<InputAPI>): void {
    this.validateFieldOnBlur(VitalSignAttributes.QT_PA_SISTOLICA, Number(event.target.getValue()), this.fieldPasApi);
    this.calculateMAP();
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.PAD_INPUT })
  onBlurPadInput(event: ChangeEvent<InputAPI>): void {
    this.validateFieldOnBlur(VitalSignAttributes.QT_PA_DIASTOLICA, Number(event.target.getValue()), this.fieldPadApi);
    this.calculateMAP();
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.PAM_INPUT })
  onBlurPamInput(event: ChangeEvent<InputAPI>): void {
    this.validateFieldOnBlur(VitalSignAttributes.QT_PAM, Number(event.target.getValue()), this.fieldPamApi);
    this.calculatePPC();
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.HEART_RATE_INPUT })
  onBlurHeartRateInput(event: ChangeEvent<InputAPI>): void {
    this.validateFieldOnBlur(VitalSignAttributes.QT_FREQ_CARDIACA, Number(event.target.getValue()), this.fieldHeartRateApi);
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.RESPIRATORY_FREQUENCY_INPUT })
  onBlurRespiratoryFrequencyInput(event: ChangeEvent<InputAPI>): void {
    this.validateFieldOnBlur(VitalSignAttributes.QT_FREQ_RESP, Number(event.target.getValue()), this.fieldRespiratoryFrequencyApi);
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.TEMPERATURE_INPUT })
  onBlurTemperatureInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    const formattedValue = value.replace(/,/g, '.');
    event.target.setValue(formattedValue);
    this.validateFieldOnBlur(VitalSignAttributes.QT_TEMP, Number(formattedValue), this.fieldTemperatureApi);
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.PAIN_SCALE_INPUT })
  onBlurPainScaleInput(event: ChangeEvent<InputAPI>): void {
    this.validateFieldOnBlur(VitalSignAttributes.QT_ESCALA_DOR, Number(event.target.getValue()), this.fieldPainScaleApi);
  }

  @OnChange({ target: VitalSignCreateAndEditAliases.CD_PAIN_SCALE_FIELD })
  onChangePainScaleFields(event: ChangeEvent<FieldAPI>): void {
    if(event.value) {
      ValidateTypeScale.valdiate(String(event.value), this.inputPainScaleApi);
    } else {
      this.inputPainScaleApi.setValue('');
    }
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.SATURATION_O2_INPUT })
  onBlurSaturationO2Input(event: ChangeEvent<InputAPI>): void {
    this.validateFieldOnBlur(VitalSignAttributes.QT_SATURACAO_O2, Number(event.target.getValue()), this.fieldSaturationO2Api);
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.CAPILLARY_BLOOD_GLUCOSE_INPUT })
  onBlurCapillaryBloodGlucoseInput(event: ChangeEvent<InputAPI>): void {
    this.validateFieldOnBlur(VitalSignAttributes.QT_GLICEMIA_CAPILAR, Number(event.target.getValue()), this.fieldCapillaryBloodGlucoseApi);
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.PVC_CMH2O_INPUT })
  onBlurPvcCmh2oInput(event: ChangeEvent<InputAPI>): void {
    this.validateFieldOnBlur(VitalSignAttributes.QT_PVC_H2O, Number(event.target.getValue()), this.fieldPvcCmh2oApi);
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.PVC_MMHF_INPUT })
  onBlurPvcMmhgInput(event: ChangeEvent<InputAPI>): void {
    this.validateFieldOnBlur(VitalSignAttributes.QT_PVC, Number(event.target.getValue()), this.fieldPvcMmhfApi);
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.PAE_MMHF_INPUT })
  onBlurPaeMmhgInput(event: ChangeEvent<InputAPI>): void {
    this.validateFieldOnBlur(VitalSignAttributes.QT_PAE, Number(event.target.getValue()), this.fieldPaeMmhfApi);
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.PIA_CMH2O_INPUT })
  onBlurPiaCmh2oInput(event: ChangeEvent<InputAPI>): void {
    this.validateFieldOnBlur(VitalSignAttributes.QT_PRESSAO_INTRA_ABD_H2O, Number(event.target.getValue()), this.fieldPiaCmh2oApi);
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.PIC_MMHG_INPUT })
  onBlurPicMmhgInput(event: ChangeEvent<InputAPI>): void {
    this.validateFieldOnBlur(VitalSignAttributes.QT_PRESSAO_INTRA_CRANIO, Number(event.target.getValue()), this.fieldPicMmhgApi);
    this.calculatePPC();
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.DEGREES_INPUT })
  onBlurDegreesInput(event: ChangeEvent<InputAPI>): void {
    this.validateFieldOnBlur(VitalSignAttributes.QT_ANGULO_CABECEIRA, Number(event.target.getValue()), this.fieldDegreesApi);
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.SC_INPUT })
  onBlurScInput(event: ChangeEvent<InputAPI>): void {
    this.validateFieldOnBlur(VitalSignAttributes.QT_SUPERF_CORPORIA, Number(event.target.getValue()), this.fieldScApi);
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.CEPHALIC_INPUT })
  onBlurCephalicInput(event: ChangeEvent<InputAPI>): void {
    this.validateFieldOnBlur(VitalSignAttributes.QT_PERIMETRO_CEFALICO, Number(event.target.getValue()), this.fieldCephalicApi);
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.INCUBATOR_TEMPERATURE_INPUT })
  onBlurIncubatorTemperatureInput(event: ChangeEvent<InputAPI>): void {
    this.validateFieldOnBlur(VitalSignAttributes.QT_TEMP_INCUBADORA, Number(event.target.getValue()), this.fieldIncubatorTemperatureApi);
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.OBSTETRIC_RECORDS_INPUT })
  onBlurObstetricRecordsInput(event: ChangeEvent<InputAPI>): void {
    this.validateFieldOnBlur(VitalSignAttributes.QT_BCF, Number(event.target.getValue()), this.fieldObstetricRecordsApi);
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.OBSTETRIC_RECORDS_BCF2_INPUT })
  onBlurObstetricRecordsBcf2Input(event: ChangeEvent<InputAPI>): void {
    this.validateFieldOnBlur(VitalSignAttributes.QT_BCF_2, Number(event.target.getValue()), this.fieldObstetricRecordsBcf2Api);
  }

  @OnBlur({ target: VitalSignCreateAndEditAliases.OBSTETRIC_RECORDS_BCF3_INPUT })
  onBlurObstetricRecordsBcf3Input(event: ChangeEvent<InputAPI>): void {
    this.validateFieldOnBlur(VitalSignAttributes.QT_BCF_3, Number(event.target.getValue()), this.fieldObstetricRecordsBcf3Api);
  }

  @OnClick({ target: VitalSignCreateAndEditAliases.DELETE_ACTION })
  onClickActionDelete(): void {
    this.deleteVitalSignDialog();
  }

  @OnClick({ target: VitalSignCreateAndEditAliases.INATIVATE_ACTION })
  onClickActionInativate(): void {
    this.inativateVitalSignModal();
  }

  @OnChange({ target: VitalSignCreateAndEditAliases.DYNAMIC_DATA_TIME_INPUT })
  onDataTimeInputChange(evt: ChangeEvent<InputAPI>): void {
    const newDateState = moment(String(evt.value));
    this.context.set({dtSinalVital: newDateState});
  }

  @OnBeforeSubmit({ target: VitalSignCreateAndEditAliases.FORM })
  async onBeforeSubmitForm(event: AbortableSubmitEvent): Promise<void> {
    const painScaleCode  = this.fieldCdPainScaleApi.isVisible() && this.fieldCdPainScaleApi.getValue();
    const painScaleValue = this.fieldPainScaleApi.isVisible()   && this.fieldPainScaleApi.getValue() === undefined;
    const {encounterId}   = this.historyService.getCurrentRouteParams()

    if (painScaleCode && painScaleValue) {
      this.inputPainScaleApi.setFocus(true);
      return
    }

    this.panelSuccessLoaderApi.setVisible(true);
    this.formDisabled(true);

    const formData = this.formVitalSignApi.getRecord();
    const { vitalSignId } = this.historyService.getCurrentRouteParams();
    const dateTime = moment(formData.DT_SINAL_VITAL as string).utc().format(ISO_FULL_DATE_FORMAT);
    const formInfos = this.createFormInfos(event);
    const imcResult = this.calculateIMC(formInfos);
    const imcData = {
      nmAttribute: 'QT_IMC',
      value: imcResult
    };
    const temperatureUnity = (formData.IE_UM_TEMP as DomainValue)?.cd;
    const temperatureData = {
      nmAttribute: 'IE_UM_TEMP',
      value: temperatureUnity
    };
    const heightUnitValue = this.context.get('heightUnitValue');
    const heightUnit = {
      nmAttribute: 'IE_UNID_MED_ALTURA',
      value: heightUnitValue
    }
    const weightUnitValue = this.context.get('weightUnitValue');
    const weightUnit = {
      nmAttribute: 'IE_UNID_MED_PESO',
      value: weightUnitValue
    }

    let notIsOutLimit = true
    const outLimitData = this.context.getBulkData().responseBodyComponent as Metadata;

    for (const fieldSet of outLimitData.children) {
      for (const field of fieldSet.children) {
        const fieldEntry = field.entry as string;
        const outLimitIsNumber = !!Number(this.findAttribute(formInfos, fieldEntry));

        if (outLimitIsNumber) {
          const getAttribute = await HttpService.get(Endpoint.GET_VITAL_SIGN_ATTRIBUTE_OUT_LIMIT, {attributeName: fieldEntry, attributeValue: this.findAttribute(formInfos, fieldEntry), encounterId })
            .then((response) => {
              const { isOutLimit } = response.body as { isOutLimit: string }

              if (isOutLimit === 'B') {
                const message = field.label + ' - ' + MessageService.translate('text.invalid-value');
                this.panelSuccessLoaderApi.setVisible(false);
                this.formDisabled(false);
                ShowToast.open(message, ToastLevel.WARNING);
                return false;
              }

              return true;
            })

          notIsOutLimit = getAttribute;

          if (!notIsOutLimit) {
            this.panelSuccessLoaderApi.setVisible(false);
            this.formDisabled(false);
            return;
          }
        }
      }
    }

    formInfos.push(imcData);
    formInfos.push(temperatureData);
    formInfos.push(heightUnit);
    formInfos.push(weightUnit);

    const body = {
      dtSinalVital: dateTime,
      nrSinalVital: vitalSignId ? vitalSignId : null,
      vitalSignAttributes: [ ...formInfos ]
    } as Record<string, Value>;

    if (notIsOutLimit) {
      notIsOutLimit = true
      event.abort(this.submitFormData(body));
    }
  }

  calculateIMC(formInfos: VitalSignSubmitProps[]): number | undefined {
    const undefinedComparsion = 'undefined';
    const defaultHeightUnit = 'm';
    const heightUnit = String(this.findAttribute(formInfos, 'IE_UNID_MED_ALTURA'));
    const unitMeasureHeight = heightUnit === undefinedComparsion ? defaultHeightUnit : heightUnit;
    const defaultWeightUnit = 'Kg';
    const weightUnit = String(this.findAttribute(formInfos, 'IE_UNID_MED_PESO'));
    const unitMeasureWeight = weightUnit === undefinedComparsion ? defaultWeightUnit : weightUnit;
    const height = unitMeasureHeight === defaultHeightUnit ? Number(this.findAttribute(formInfos, 'QT_ALTURA_M')) : Number(this.findAttribute(formInfos, 'QT_ALTURA_CM'));
    const weight = unitMeasureWeight === defaultWeightUnit ? Number(this.findAttribute(formInfos, 'QT_PESO')) : Number(this.findAttribute(formInfos, 'QT_PESO_ATUAL'));
    const imcResult = CalculateIMC.calculate({
      height,
      unitMeasureHeight,
      unitMeasureWeight,
      weight
    });
    return imcResult;
  }

  @OnCancel({ target: VitalSignCreateAndEditAliases.FORM })
  onCancelForm(): void {
    this.discardChangesDialog();
  }

  @OnFocus({ target: VitalSignCreateAndEditAliases.PAIN_SCALE_INPUT })
  onFocusPainScaleField(): void {
    const cdPainScaleField = this.fieldCdPainScaleApi.getValue() as CdScalePain;
    ValidateTypeScale.valdiate(cdPainScaleField?.value, this.inputPainScaleApi);
  }

  @OnAfterFetch(VitalSignCreateAndEditAliases.TEMPERATURE_UNITY_FETCH)
  handleAfterFetchTemperature(event: FetchEvent): void {
    const defaultValue        = (event.data as Record<string, Value>[])[0];
    const defaultTemperature  = 37;
    const { isDetails } = this.historyService.getCurrentRouteParams();
    const isDetailsScreen = isDetails === YesNoType.YES;
    this.fieldTemperatureApi.setValue(defaultTemperature);
    if (!isDetailsScreen) {
      this.autocompleteTemperatureUnityAPI.setValue(defaultValue);
    }
  }

  formDisabled(isDisabled: boolean): void {
    this.formVitalSignApi.setDisabled(isDisabled);
    this.formVitalSignApi.setEditing(!isDisabled);
  }
}

export default VitalSignCreateAndEditController;
